import React, { useState, useMemo, useRef, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { LineChart } from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts";
import { useYearFilter } from "../../hooks/useYearFilter";
import { formatCurrency, formatCompactNumber } from "../../../../../utils";
import { BREAKPOINTS, getThemeColors } from "../../../../../utils/constants";
import { RootState } from "../../../../../redux/store";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  SectionCard,
  SectionHeader,
  TitleBlock,
  SectionTitle,
  SubtitleText,
  ChartEmptyState,
  ChartErrorState,
} from "../../styles";
import { YearFilter } from "../YearFilter";
import { useGetAccountBalanceHistoryQuery } from "../../../../../services/accounts";
import Button, { ButtonTypes } from "../../../../../components/Button";
import type { BalanceHistorySectionProps, PeriodData } from "./types";
import { IncomeSpan, ExpensesSpan, NetSpan, ChartFill } from "./styles";

const EMPTY_PERIOD: PeriodData = { points: [], income: 0, expenses: 0, net: 0 };

export const BalanceHistorySection: React.FC<BalanceHistorySectionProps> = ({
  accountId,
  availableYears,
}) => {
  const theme = useSelector((state: RootState) => state.theme);
  const themeColors = getThemeColors(theme?.theme);
  const yearFilter = useYearFilter(availableYears);
  const { selectedYear } = yearFilter;
  const {
    data,
    isError: hasError,
    refetch,
  } = useGetAccountBalanceHistoryQuery({
    accountId,
    year: selectedYear ?? undefined,
  });
  const periodData = data ?? EMPTY_PERIOD;
  const isMobile = useResponsive(BREAKPOINTS.tabletMin);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(isMobile ? 250 : 300);

  useLayoutEffect(() => {
    const el = chartContainerRef.current;
    if (!el) {
      return;
    }
    const applyHeight = (h: number) => {
      if (h > 0) {
        setChartHeight(h);
      }
    };
    // Seed from the measured container before paint to avoid a first-frame
    // flash at the responsive guess, then keep it in sync on resize.
    applyHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(([entry]) => {
      applyHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleRetry = () => {
    void refetch();
  };

  const xLabels = useMemo(
    () => periodData.points.map((p) => p.label),
    [periodData],
  );
  const values = useMemo(
    () => periodData.points.map((p) => p.balance),
    [periodData],
  );

  const { min, max } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const v of values) {
      if (v < lo) {
        lo = v;
      }
      if (v > hi) {
        hi = v;
      }
    }
    return { min: lo, max: hi };
  }, [values]);

  const zeroPct = useMemo(() => {
    if (min >= 0) {
      return 100;
    }
    if (max <= 0) {
      return 0;
    }
    return Math.round((max / (max - min)) * 100);
  }, [min, max]);

  const gradientId = useMemo(
    () => `balance-gradient-${accountId}`,
    [accountId],
  );

  const netPositive = periodData.net >= 0;
  const netSign = netPositive ? "+" : "";

  const chartSx = useMemo(
    () => ({
      "& .MuiLineChart-area": { fill: `url(#${gradientId})` },
      "& .MuiChartsAxis-tickLabel": {
        fontFamily: "'Crimson Pro', sans-serif",
        fill: "var(--color-text-default) !important",
      },
      "& .MuiChartsAxis-tick": {
        stroke: "var(--color-text-default) !important",
      },
      "& .MuiChartsAxis-line": {
        stroke: "var(--color-text-default) !important",
      },
    }),
    [gradientId],
  );

  const series = useMemo(
    () => [
      {
        data: values,
        area: true,
        color: themeColors.statusSuccess,
        showMark: true,
        valueFormatter: (v: number | null) =>
          v === null ? "" : formatCurrency(v),
      },
    ],
    [values, themeColors.statusSuccess],
  );

  const xAxisConfig = useMemo(
    () => [{ data: xLabels, scaleType: "point" as const }],
    [xLabels],
  );

  const yAxisConfig = useMemo(
    () => [
      {
        valueFormatter: (v: number | null) =>
          v === null ? "" : formatCompactNumber(v),
        width: isMobile ? 36 : 44,
      },
    ],
    [isMobile],
  );

  const chartMargin = useMemo(
    () =>
      isMobile
        ? { bottom: 4, left: 0, right: 8, top: 8 }
        : { bottom: 4, left: 0, right: 12, top: 8 },
    [isMobile],
  );

  const renderChartBody = () => {
    if (hasError) {
      return (
        <ChartErrorState role="alert">
          Couldn&apos;t load balance history.
          <Button type={ButtonTypes.cancel} size="small" onClick={handleRetry}>
            Try again
          </Button>
        </ChartErrorState>
      );
    }

    if (periodData.points.length === 0) {
      return (
        <ChartEmptyState>No balance history for this period</ChartEmptyState>
      );
    }

    return (
      <ChartFill ref={chartContainerRef}>
        <LineChart
          aria-label="Balance history over time"
          height={chartHeight}
          series={series}
          xAxis={xAxisConfig}
          yAxis={yAxisConfig}
          sx={chartSx}
          margin={chartMargin}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset={`${zeroPct}%`}
                stopColor={themeColors.statusSuccess}
                stopOpacity={0.35}
              />
              <stop
                offset={`${zeroPct}%`}
                stopColor={themeColors.statusError}
                stopOpacity={0.25}
              />
            </linearGradient>
          </defs>
          {/* lineStyle/labelStyle are MUI X Charts API props, not HTML style
              attributes. The labelled break-even line names the above/below-zero
              split in text, so the green/red gradient is not the only cue. */}
          <ChartsReferenceLine
            y={0}
            label="Break-even (€0)"
            labelAlign="start"
            lineStyle={{
              stroke: "var(--color-border)",
              strokeDasharray: "4 4",
              strokeWidth: 1.5,
            }}
            labelStyle={{
              fill: "var(--color-text-default)",
              fontFamily: "'Crimson Pro', sans-serif",
              fontSize: 12,
            }}
          />
        </LineChart>
      </ChartFill>
    );
  };

  return (
    <SectionCard>
      <SectionHeader>
        <TitleBlock>
          <SectionTitle>Balance history</SectionTitle>
          <SubtitleText>
            <IncomeSpan>Income {formatCurrency(periodData.income)}</IncomeSpan>
            {" · "}
            <ExpensesSpan>
              Expenses {formatCurrency(periodData.expenses)}
            </ExpensesSpan>
            {" · "}
            <NetSpan $positive={netPositive}>
              Net {netSign}
              {formatCurrency(periodData.net)}
            </NetSpan>
          </SubtitleText>
        </TitleBlock>
        <YearFilter {...yearFilter} />
      </SectionHeader>

      {renderChartBody()}
    </SectionCard>
  );
};
